import {
  MessageConversationTypeEnum,
  MessageConversationSubscription,
  authMeQuery,
  useMessageConversationSubscription,
} from '@/types/generated/graphql';
import { useMatch } from 'react-router-dom';
import { recentMessageHandler } from './recent-message-handler';
import { messageConversationHandler } from './message-conversation-handler';
import { CALL_STATUS, MESSAGE_STATUS } from '@shared/constants';
import { cache } from '@/container/apollo-client/cache';
import { AUTH_ME } from '@/graphql/query.graphql';
import { CallStatus, MessageStatus } from '@/types/app.types';
import { useReadMessage } from '@modules/conversations/hooks/use-read-message';
import { incomingStore } from '@modules/phone/store/incoming.store';
import { switchCallHandler } from './switch-call-handler';

export const useConversationSubscription = () => {
  const matchConversation = useMatch({
    path: '/conversations/:channelId/:clientId?', // `?` makes clientId optional and handles the case in which there is no clientId
    end: false,
  });
  const channelId = matchConversation?.params?.channelId + '';
  const clientId = matchConversation?.params?.clientId + '';

  const { readMessage } = useReadMessage();

  useMessageConversationSubscription({
    onData: (res) =>
      handleOnData({
        data: res?.data?.data as MessageConversationSubscription,
        channelId,
        clientId,
        readMessage,
      }),
  });
};

function handleOnData({
  data,
  channelId,
  clientId,
  readMessage,
}: {
  data: MessageConversationSubscription;
  channelId: string;
  clientId: string;
  readMessage: (messageConversationId: number) => void;
}) {
  const chatbox = document.getElementById('chat-box');

  const profile: authMeQuery | null = cache.readQuery({ query: AUTH_ME });
  const isSelfSender = profile?.authMe?.data?.user?.id === data?.messageConversation?.agent?.user?.id;
  // ignore if not in conversation page
  const isInConversationPage = window.location.pathname.startsWith('/conversations');
  // to ignore messageConversation if on different channel
  const isChatOpen =
    data?.messageConversation?.client?.id === +clientId && data?.messageConversation?.channelId === +channelId;

  switchCallHandler(data);

  if (!isInConversationPage || shouldSkip(data)) return;

  messageConversationHandler(data);

  recentMessageHandler(data, channelId, clientId);
  if (isChatOpen && !isSelfSender) {
    readMessage(data?.messageConversation?.id ?? 0);
    // scroll to bottom on new message if  scrolled limited
    if ((chatbox?.scrollHeight || 0) < 1000) {
      chatbox?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}

export function shouldSkip(data: MessageConversationSubscription) {
  const { setRejectedMessage } = incomingStore.getState();

  let shouldSkip = false;
  const profile: authMeQuery | null = cache.readQuery({ query: AUTH_ME });
  const isSelfSender = profile?.authMe?.data?.user?.id === data?.messageConversation?.agent?.user?.id;
  const conversationType = data?.messageConversation?.conversationType;
  const conversationStatus = data?.messageConversation?.conversationStatus as CallStatus | MessageStatus;
  const ignoreMessageStatuses: MessageStatus[] = [
    MESSAGE_STATUS.DELIVERED,
    MESSAGE_STATUS.FAILED,
    MESSAGE_STATUS.UNDELIVERED,
  ];
  const ignoreCallStatuses: CallStatus[] = [CALL_STATUS.ORIGINATED];
  const ignoreMessageStatus = ignoreMessageStatuses.includes(conversationStatus as MessageStatus);
  const ignoreCallStatus = ignoreCallStatuses.includes(conversationStatus as CallStatus);
  const isMessageSubscription = conversationType === MessageConversationTypeEnum.message;
  const isCallSubscription = conversationType === MessageConversationTypeEnum.conference;

  if (isMessageSubscription) {
    // when user rejects the call from the incoming call
    if (isSameAsRejectedMessage(data)) {
      shouldSkip = false;
      setRejectedMessage({});
    } else if (isSelfSender && conversationStatus !== MESSAGE_STATUS.SENT) {
      shouldSkip = true; // in this case it is already attached on sendMessage response
    }
  }
  // subscription statuses are ignorable
  if (isMessageSubscription && ignoreMessageStatus) {
    shouldSkip = true;
  }
  if (isCallSubscription && ignoreCallStatus) {
    shouldSkip = true;
  }

  return shouldSkip;
}

export function isSameAsRejectedMessage(data: MessageConversationSubscription) {
  const { rejectedMessage } = incomingStore.getState();
  const message = data?.messageConversation;

  if (Number(message?.numMedia) > 0) return false; // in case there is media return false
  if (
    message?.channelId === Number(rejectedMessage?.channelId) &&
    message?.client?.id === Number(rejectedMessage?.clientId) &&
    message?.messageContent === rejectedMessage?.message
  ) {
    return true;
  }
  return false;
}
